import React, { useState, useRef, useEffect } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_ENDPOINTS, getApiUrl, apiRequest } from '../../config/api';
import { parseFlashcards, parseFlashcardFile, FLASHCARD_TEMPLATE } from '../../utils/flashcardImport';
import { downloadText } from '../../utils/download';
import { AdProcessingBanner } from '../ads/Ads';
import MathText from '../../utils/MathText';
import './Modules.css';

// PDF-only upload limits (must match the backend caps).
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

// Progress curve for the single PDF->flashcards request: start at 8% and ease
// asymptotically toward 90% so the bar keeps moving until the server responds.
const PROGRESS_HEAD_START = 8;
const PROGRESS_CEILING = 90;
const PROGRESS_TIME_CONSTANT_MS = 20000;

const statusForProgress = (pct: number): string => {
  if (pct < 35) return 'Reading the pages of your PDF with AI\u2026';
  if (pct < 65) return 'Transcribing and organising the content\u2026';
  if (pct < 85) return 'Generating your flashcards\u2026';
  return 'Still working \u2014 long documents take a little longer\u2026';
};

interface FlashcardSet {
  id: number;
  title: string;
  description: string;
  card_count: number;
  created_at: string;
  last_studied?: string;
  total_study_sessions: number;
  average_score: number;
  time_limit_minutes?: number;
}

interface StudySession {
  id: number;
  flashcard_set: FlashcardSet;
  score: number;
  total_cards: number;
  completed_at: string;
}

interface Flashcard {
  id: number;
  question: string;
  answer: string;
  times_studied?: number;
  is_correct?: boolean;
}

interface StudyHistory {
  flashcard_sets: FlashcardSet[];
  recent_sessions: StudySession[];
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  page_size: number;
  has_next: boolean;
  has_previous: boolean;
}

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

const Toast: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${toast.type}`}>
      <div className="toast-message">{toast.message}</div>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
};

// Simple outline trash icon (24x24) for delete actions
const TrashIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14H5V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
    <polyline points="9 6 9 4 15 4 15 6" />
  </svg>
);

// Simple outline refresh/clear icon (24x24) for clear actions
const ClearIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

// Added vector icons for edit and create actions
const EditIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const PlusIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ReciteModule: React.FC = () => {
  const { t } = useLanguage();
  // Get initial view from URL or default to 'main'
  const getInitialView = (): 'main' | 'upload' | 'import' | 'generate' | 'select' | 'study' | 'edit' => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view && ['main', 'upload', 'import', 'generate', 'select', 'study', 'edit'].includes(view)) {
      return view as any;
    }
    return 'main';
  };
  
  const [currentView, setCurrentView] = useState<'main' | 'upload' | 'import' | 'generate' | 'select' | 'study' | 'edit'>(getInitialView);
  
  // Update URL when view changes
  const updateView = (view: 'main' | 'upload' | 'import' | 'generate' | 'select' | 'study' | 'edit') => {
    setCurrentView(view);
    
    // Reset editing states when leaving edit view
    if (view !== 'edit') {
      setEditingSetIdState(null);
      setEditingTitle('');
      setActiveEditIds(new Set());
    }

    // Always close inline title editing when changing view
    setTitleEditingSetId(null);
    setEditingTitle('');
    
    const url = new URL(window.location.href);
    if (view === 'main') {
      url.searchParams.delete('view');
    } else {
      url.searchParams.set('view', view);
    }
    window.history.replaceState({}, '', url.toString());
  };
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationPct, setGenerationPct] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  // "Use my own cards" import state
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const importFileRef = useRef<HTMLInputElement>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStartRef = useRef<number>(0);
  const [currentFlashcardSet, setCurrentFlashcardSet] = useState<FlashcardSet | null>(null);
  const [currentFlashcards, setCurrentFlashcards] = useState<Flashcard[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [studyHistory, setStudyHistory] = useState<StudyHistory | null>(null);
  const [autoShowTimer, setAutoShowTimer] = useState<NodeJS.Timeout | null>(null);

  // Card selection workflow
  const [generatedCards, setGeneratedCards] = useState<Flashcard[]>([]);
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  // How many cards the user asks the AI to generate from the PDF.
  const [cardCount, setCardCount] = useState<number>(20);
  // Non-blocking notice shown on the select page (e.g. fewer cards than requested).
  const [countNotice, setCountNotice] = useState<string>('');

  const [editingSetId, setEditingSetIdState] = useState<number | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'edit' && params.get('set_id')) {
      return Number(params.get('set_id'));
    }
    return null;
  });
  const [titleEditingSetId, setTitleEditingSetId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [editingCards, setEditingCards] = useState<Flashcard[]>([]);
  const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);
  const [activeEditIds, setActiveEditIds] = useState<Set<number>>(new Set());
  
  // Toast notifications
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteSetId, setDeleteSetId] = useState<number | null>(null);
  
  // Clear confirmation modal
  const [showClearConfirm, setShowClearConfirm] = useState<boolean>(false);
  const [clearSetId, setClearSetId] = useState<number | null>(null);
  
  // Per-card delete confirmation
  const [showCardDeleteConfirm, setShowCardDeleteConfirm] = useState<boolean>(false);
  const [cardToDelete, setCardToDelete] = useState<Flashcard | null>(null);
  
  // Pagination and search
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag-and-drop reorder handling (card selection screen)
  const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);

  const stopProgress = () => {
    if (progressTimerRef.current !== null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  // Drive the bar on an elapsed-time easing curve until the response lands.
  const startProgress = () => {
    stopProgress();
    progressStartRef.current = Date.now();
    setGenerationPct(PROGRESS_HEAD_START);
    setStatusMessage(statusForProgress(PROGRESS_HEAD_START));
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - progressStartRef.current;
      const pct =
        PROGRESS_HEAD_START +
        (PROGRESS_CEILING - PROGRESS_HEAD_START) *
          (1 - Math.exp(-elapsed / PROGRESS_TIME_CONSTANT_MS));
      setGenerationPct(pct);
      setStatusMessage(statusForProgress(pct));
    }, 200);
  };

  useEffect(() => stopProgress, []);

  useEffect(() => {
    // Auto-show answer after 3 minutes
    if (currentView === 'study' && !showAnswer) {
      const timer = setTimeout(() => {
        setShowAnswer(true);
      }, 3 * 60 * 1000); // 3 minutes
      setAutoShowTimer(timer);
    }

    return () => {
      if (autoShowTimer) {
        clearTimeout(autoShowTimer);
      }
    };
  }, [currentView, currentCardIndex, showAnswer]);

  const fetchStudyHistory = async (page: number = 1, search: string = '') => {
    try {
      const params = new URLSearchParams();
      if (page > 1) params.append('page', page.toString());
      if (search.trim()) params.append('search', search.trim());
      
      const url = getApiUrl(API_ENDPOINTS.STUDY_HISTORY) + (params.toString() ? `?${params.toString()}` : '');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setStudyHistory({
          flashcard_sets: data.flashcard_sets,
          recent_sessions: data.recent_sessions
        });
        setPaginationInfo(data.pagination);
        setCurrentPage(data.pagination.current_page);
        // Ensure inline title edit is closed after refresh
        setTitleEditingSetId(null);
        setEditingTitle('');
      } else {
        console.error('Failed to fetch study history');
      }
    } catch (error) {
      console.error('Error fetching study history:', error);
    }
  };

  // Stage a chosen/dropped PDF (validate type/size) without processing it yet.
  const stageFile = (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      alert('Only PDF files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast('File size too large. Maximum size is 25MB.', 'error');
      return;
    }
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) stageFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isGenerating) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && !isGenerating) stageFile(file);
  };

  // Submit the staged PDF: the backend reads every page with the vision model,
  // transcribes it, chunks the text (when large) and generates the flashcards.
  const generateFlashcards = async () => {
    const file = selectedFile;
    if (!file) {
      showToast('Please choose a PDF first.', 'error');
      return;
    }

    setIsGenerating(true);
    setCountNotice('');
    startProgress();

    const form = new FormData();
    form.append('file', file);
    form.append('count', String(cardCount));

    try {
      const res = await apiRequest<{
        flashcards: { question: string; answer: string }[];
        card_count: number;
        requested_count?: number;
        pages_read?: number;
        total_pages?: number;
        truncated?: boolean;
      }>(API_ENDPOINTS.FLASHCARDS_FROM_PDF, {
        method: 'POST',
        body: form,
        timeout: 20 * 60 * 1000,
      });

      const cards = (res.data?.flashcards || []).filter(
        (c) => c && (c.question || '').trim() && (c.answer || '').trim()
      );
      if (!cards.length) {
        showToast('No flashcards could be generated from that PDF. Please try another file.', 'error');
        return;
      }

      const tempFlashcards: Flashcard[] = cards.map((card, index) => ({
        id: index + 1,
        question: card.question,
        answer: card.answer,
        times_studied: 0,
        is_correct: undefined,
      }));

      setGeneratedCards(tempFlashcards);
      setCurrentFlashcardSet(null);
      setSelectedCards(new Set<number>(tempFlashcards.map((card) => card.id)));
      setGenerationPct(100);

      // Surface a friendly notice when the document couldn't supply the
      // requested number of distinct cards (limited material / page cap).
      const requested = res.data?.requested_count || cardCount;
      const notices: string[] = [];
      if (requested > tempFlashcards.length) {
        notices.push(
          `You asked for ${requested} cards, but the document only yielded ${tempFlashcards.length} ` +
            `distinct one${tempFlashcards.length === 1 ? '' : 's'}. ` +
            `The number of cards is limited by the amount of content in the PDF — try a longer or denser document, or lower the card count.`
        );
      }
      if (res.data?.truncated) {
        notices.push(
          `Only the first ${res.data.pages_read ?? ''} of ${res.data.total_pages ?? ''} pages were read, ` +
            `which may also limit the number of cards.`
        );
      }
      setCountNotice(notices.join(' '));

      showToast(`Generated ${tempFlashcards.length} flashcards! Review and select the ones to keep.`, 'success');
      updateView('select');
    } catch (err) {
      console.error('Generation failed', err);
      const serverMsg = err instanceof Error ? err.message : '';
      showToast(
        (serverMsg && serverMsg !== 'API request failed' ? `${serverMsg} ` : '') +
          'Could not generate flashcards from that PDF. Make sure it is a valid PDF and try again.',
        'error'
      );
    } finally {
      stopProgress();
      setIsGenerating(false);
      setStatusMessage('');
      setIsDragging(false);
    }
  };

  // Fisher–Yates shuffle (returns a new array; does not mutate the source).
  const shuffleCards = (cards: Flashcard[]): Flashcard[] => {
    const arr = [...cards];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // No scoring or timer: "Next" simply advances, and finishing returns to the menu.
  const nextCard = () => {
    if (currentCardIndex >= currentFlashcards.length - 1) {
      showToast('Revision completed! 🎉', 'success');
      updateView('main');
    } else {
      setCurrentCardIndex(currentCardIndex + 1);
      setShowAnswer(false);
    }
  };

  const getCSRFToken = async () => {
    try {
      console.log('Fetching CSRF token...');
      const response = await fetch(getApiUrl(API_ENDPOINTS.CSRF_TOKEN), {
        credentials: 'include',
      });
      console.log('CSRF response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('CSRF token received:', data.csrfToken ? 'Present' : 'Missing');
        return data.csrfToken;
      } else {
        console.error('CSRF token request failed:', response.status);
      }
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
    }
    return null;
  };

  const toggleCardSelection = (cardId: number) => {
    const newSelectedCards = new Set(selectedCards);
    if (newSelectedCards.has(cardId)) {
      newSelectedCards.delete(cardId);
    } else {
      newSelectedCards.add(cardId);
    }
    setSelectedCards(newSelectedCards);
  };

  const selectAllCards = () => {
    const allCardIds = new Set<number>(generatedCards.map(card => card.id));
    setSelectedCards(allCardIds);
  };

  const deselectAllCards = () => {
    setSelectedCards(new Set());
  };

  // Start the in-browser revision flow for any list of cards (AI-generated or
  // imported from the user's own material). Cards are shuffled each time; there
  // is no timer and no performance tracking. Nothing is saved server-side.
  const startRevision = (cards: Flashcard[], title: string, docName: string = 'document') => {
    const shuffled = shuffleCards(cards);
    const sessionSet: FlashcardSet = {
      id: 0,
      title,
      document_name: docName,
      card_count: shuffled.length,
      created_at: new Date().toISOString(),
      last_studied: null,
      progress: 0,
      is_active: true,
      flashcards: shuffled,
    } as unknown as FlashcardSet;

    setCurrentFlashcardSet(sessionSet);
    setCurrentFlashcards(shuffled);
    setCurrentCardIndex(0);
    setShowAnswer(false);
    setCurrentView('study');
    showToast(`Revising ${shuffled.length} flashcards (shuffled).`, 'success');
  };

  // Download the selected generated cards as a .txt in the Q:/A: format accepted
  // by the "Use My Own Cards" page.
  const downloadSelectedCardsTxt = () => {
    const chosen = generatedCards.filter((card) => selectedCards.has(card.id));
    if (!chosen.length) {
      showToast('Select at least one card to download.', 'error');
      return;
    }
    const oneLine = (s: string) => (s || '').replace(/\s*\n\s*/g, ' ').trim();
    const body = chosen
      .map((c) => `Q: ${oneLine(c.question)}\nA: ${oneLine(c.answer)}`)
      .join('\n\n');
    const base = (selectedFile?.name || 'flashcards').replace(/\.pdf$/i, '');
    downloadText(`${base || 'flashcards'}-cards.txt`, body + '\n');
    showToast(`Downloaded ${chosen.length} card${chosen.length === 1 ? '' : 's'} as a .txt file.`, 'success');
  };

  // Parse the user's own pasted material into cards and revise them directly.
  const startImportedRevision = (cards: { question: string; answer: string }[]) => {
    if (!cards.length) {
      setImportError('No valid cards found. Use the Q:/A: format or one "front<TAB>back" per line.');
      return;
    }
    setImportError('');
    const asFlashcards: Flashcard[] = cards.map((c, i) => ({ id: i + 1, question: c.question, answer: c.answer }));
    startRevision(asFlashcards, 'My Flashcards (this session)');
  };

  const handleImportText = () => {
    startImportedRevision(parseFlashcards(importText));
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const cards = await parseFlashcardFile(file);
      startImportedRevision(cards);
    } catch {
      setImportError('Could not read that file. Please try a .txt or .csv file.');
    } finally {
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours === 1) {
        return '1 hour ago';
      } else {
        return `${diffInHours} hours ago`;
      }
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    switch (extension) {
      case '.pdf':
        return '📄';
      case '.ppt':
      case '.pptx':
        return '📊';
      case '.txt':
        return '📝';
      default:
        return '📁';
    }
  };

  const renameFlashcardSet = async (setId: number, newTitle: string) => {
    try {
      const csrfToken = await getCSRFToken();
      const response = await fetch(getApiUrl(`/api/recite/flashcard-sets/${setId}/rename/`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({ title: newTitle }),
      });

      if (response.ok) {
        fetchStudyHistory();
        setTitleEditingSetId(null);
        setEditingCards([]);
        showToast('Flashcard set renamed successfully!', 'success');
      } else {
        showToast('Failed to rename flashcard set. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error renaming flashcard set:', error);
      showToast('Failed to rename flashcard set. Please try again.', 'error');
    }
  };

  const deleteFlashcardSet = async (setId: number) => {
    setDeleteSetId(setId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteSetId) return;

    try {
      const csrfToken = await getCSRFToken();
      const response = await fetch(getApiUrl(API_ENDPOINTS.DELETE_FLASHCARD_SET(deleteSetId)), {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': csrfToken,
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        fetchStudyHistory();
        showToast('Flashcard set deleted successfully!', 'success');
      } else {
        showToast('Failed to delete flashcard set. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error deleting flashcard set:', error);
      showToast('Failed to delete flashcard set. Please try again.', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setDeleteSetId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteSetId(null);
  };

  const clearFlashcardSet = async (setId: number) => {
    setClearSetId(setId);
    setShowClearConfirm(true);
  };

  const confirmClear = async () => {
    if (!clearSetId) return;

    try {
      const csrfToken = await getCSRFToken();
      const response = await fetch(getApiUrl(API_ENDPOINTS.CLEAR_FLASHCARD_SET(clearSetId)), {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        fetchStudyHistory();
        showToast('Flashcard set progress cleared successfully!', 'success');
      } else {
        showToast('Failed to clear flashcard set progress. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error clearing flashcard set progress:', error);
      showToast('Failed to clear flashcard set progress. Please try again.', 'error');
    } finally {
      setShowClearConfirm(false);
      setClearSetId(null);
    }
  };

  const cancelClear = () => {
    setShowClearConfirm(false);
    setClearSetId(null);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const newToast: Toast = {
      id: Date.now().toString(),
      message,
      type
    };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-dismiss toast after 10 seconds
    setTimeout(() => {
      removeToast(newToast.id);
    }, 10000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchStudyHistory(page, searchQuery);
  };

  const fetchFlashcardSetDetails = async (setId: number) => {
    try {
      setIsLoadingEdit(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.FLASHCARD_SET(setId)), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include'
      });
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        setEditingCards(data.flashcards || []);
        setCurrentFlashcardSet(data);
      } else {
        const text = await response.text();
        console.error('Unexpected response', text);
        showToast('Failed to load flashcard set. Please try again.', 'error');
      }
    } finally {
      setIsLoadingEdit(false);
    }
  };

  // Handle loading when entering edit view
  useEffect(() => {
    if (currentView === 'edit' && editingSetId) {
      fetchFlashcardSetDetails(editingSetId);
    }
  }, [currentView, editingSetId]);

  const openEditView = (setId: number) => {
    setEditingSetIdState(setId);
    const url = new URL(window.location.href);
    url.searchParams.set('view', 'edit');
    url.searchParams.set('set_id', String(setId));
    window.history.replaceState({}, '', url.toString());
    setCurrentView('edit');
  };

  const updateOrCreateCard = async (card: Partial<Flashcard> & {id?: number}) => {
    const csrfToken = await getCSRFToken();
    if (!card.question || !card.answer) return null;
    if (!card.id || card.id < 0) {
      // create
      const response = await fetch(getApiUrl(API_ENDPOINTS.CREATE_FLASHCARD(editingSetId!)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({question: card.question, answer: card.answer})
      });
      return await response.json();
    } else {
      const response = await fetch(getApiUrl(API_ENDPOINTS.UPDATE_FLASHCARD(card.id!)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({question: card.question, answer: card.answer})
      });
      return await response.json();
    }
  };

  const deleteCardById = async (cardId: number) => {
    const csrfToken = await getCSRFToken();
    await fetch(getApiUrl(API_ENDPOINTS.DELETE_FLASHCARD(cardId)), {
      method: 'DELETE',
      headers:{
        'X-CSRFToken': csrfToken || '',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      credentials: 'include'
    });
  };

  const handleSaveChanges = async () => {
    try {
      // Iterate through editingCards and persist changes
      for (const card of editingCards) {
        if (!card.question || !card.answer) continue;
        await updateOrCreateCard(card);
      }
      showToast('Flashcards updated!', 'success');
      fetchFlashcardSetDetails(editingSetId!);
    } catch (e) {
      showToast('Failed to save changes', 'error');
    }
  };

  const handleCardDragStart = (index: number) => {
    setDragStartIndex(index);
  };

  const handleCardDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Necessary to allow a drop
  };

  const handleCardDrop = (index: number) => {
    if (dragStartIndex === null || dragStartIndex === index) return;
    setEditingCards(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(dragStartIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });
    setDragStartIndex(null);
  };

  // Toggle editing mode for a card
  const toggleCardEditing = async (cardId: number) => {
    setActiveEditIds(prev => {
      const updated = new Set(prev);
      if (updated.has(cardId)) {
        updated.delete(cardId);
      } else {
        updated.add(cardId);
      }
      return updated;
    });

    // If turning off edit mode, persist changes and sync new id
    const cardIndex = editingCards.findIndex(c => (c.id ?? editingCards.indexOf(c)) === cardId);
    if (cardIndex !== -1 && activeEditIds.has(cardId)) {
      const cardObj = editingCards[cardIndex];
      if (!cardObj.question || !cardObj.answer) return;
      try {
        const saved = await updateOrCreateCard(cardObj);
        if (saved && saved.id && saved.id !== cardObj.id) {
          setEditingCards(prev => {
            const updated = [...prev];
            updated[cardIndex] = { ...saved };
            return updated;
          });
        }
      } catch (error) {
        console.error('Failed to save card', error);
        showToast('Failed to save card', 'error');
      }
    }
  };

  // Disable all edit modes (background click)
  const disableAllEditing = async () => {
    // Persist any cards currently in edit mode
    for (const id of Array.from(activeEditIds)) {
      const cardObj = editingCards.find(c => (c.id ?? editingCards.indexOf(c)) === id);
      if (cardObj && cardObj.question && cardObj.answer) {
        try {
          await updateOrCreateCard(cardObj);
        } catch (err) {
          console.error('Failed to save card', err);
        }
      }
    }
    setActiveEditIds(new Set());
  };

  const handleEditBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // If click occurred outside any card item, disable all
    const target = e.target as HTMLElement;
    if (!target.closest('.card-item')) {
      disableAllEditing();
    }
  };

  const renderMainMenu = () => (
    <div className="recite-main-menu">
      <div className="module-header flashcard-module">
        <div className="module-icon">📚</div>
        <h1 className="module-title">{t('moduleHeaders.reciteMain.title')}</h1>
        <p className="module-subtitle">{t('moduleHeaders.reciteMain.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="recite" />
      
      <div className="recite-guidelines">
        <div className="guideline-item">
          <div className="guideline-icon">📚</div>
          <div className="guideline-content">
            <h3>Upload a PDF</h3>
            <p>AI reads every page of your PDF — including scans — and builds flashcards</p>
          </div>
        </div>
        <div className="guideline-item">
          <div className="guideline-icon">🤖</div>
          <div className="guideline-content">
            <h3>AI Generation</h3>
            <p>Automatically create flashcards from your document content</p>
          </div>
        </div>
        <div className="guideline-item">
          <div className="guideline-icon">⏱️</div>
          <div className="guideline-content">
            <h3>Study in This Tab</h3>
            <p>Flip through the cards at your own pace — no timer, nothing saved to an account</p>
          </div>
        </div>
      </div>

      <div className="recite-actions">
        <div className="action-card" onClick={() => updateView('upload')}>
          <div className="action-icon">➕</div>
          <h3>Generate with AI</h3>
          <p>Upload a PDF and let the AI read it and create flashcards</p>
        </div>
        <div className="action-card" onClick={() => updateView('import')}>
          <div className="action-icon">✍️</div>
          <h3>Use My Own Cards</h3>
          <p>Paste Q&amp;A or upload a .txt/.csv file to revise your own flashcards</p>
        </div>
      </div>
    </div>
  );

  const renderUploadSection = () => {
    // While processing, show a single progress panel instead of the form.
    if (isGenerating) {
      return (
        <div className="module-container recite-module">
          <div className="module-content">
            <div className="content-section">
              <div className="section-header">
                <h3>Generating Flashcards</h3>
                {selectedFile && <p className="file-name" style={{ marginBottom: 0 }}>{selectedFile.name}</p>}
              </div>
              <AdProcessingBanner active={isGenerating} />
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.round(generationPct)}%` }}></div>
                  <div className="progress-percentage">{Math.round(generationPct)}%</div>
                </div>
                <div className="progress-message">
                  {statusMessage || 'Reading your PDF…'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="module-container recite-module">
        <div className="module-header">
          <span className="module-icon">📄</span>
          <h2 className="module-title">{t('moduleHeaders.reciteCreate.title')}</h2>
          <p className="module-subtitle">{t('moduleHeaders.reciteCreate.subtitle')}</p>
        </div>
        <ModuleTopExtras moduleId="recite" />
        <div className="module-content">
          <div className="content-section">
            <div className="section-header">
              <h3>Upload your PDF</h3>
              <p>Every page is read by AI, then turned into question/answer flashcards. (PDF only, max 25 MB)</p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="application/pdf,.pdf"
              onChange={handleFileSelect}
            />

            <div
              className={`file-upload${isDragging ? ' dragover' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <span className="file-upload-icon">📄</span>
              <div className="file-upload-text">Drag &amp; drop your PDF here</div>
              <div className="file-upload-hint">
                {selectedFile ? selectedFile.name : 'or click to browse — PDF files only'}
              </div>
              <span className="file-upload-button">{selectedFile ? 'Replace PDF' : 'Choose PDF'}</span>
            </div>

            {selectedFile && (
              <div className="generation-actions" style={{ flexDirection: 'column', gap: '12px' }}>
                <div className="form-group" style={{ marginBottom: 0, textAlign: 'center' }}>
                  <label className="form-label" htmlFor="flashcard-count">
                    Number of cards to generate: <strong>{cardCount}</strong>
                  </label>
                  <input
                    id="flashcard-count"
                    type="range"
                    min={5}
                    max={100}
                    step={5}
                    value={cardCount}
                    onChange={(e) => setCardCount(Number(e.target.value))}
                    style={{ width: '100%', maxWidth: 420 }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 420, margin: '0 auto', fontSize: '0.8rem', color: '#888' }}>
                    <span>5</span><span>100</span>
                  </div>
                </div>
                <button className="btn btn-primary" onClick={generateFlashcards}>
                  Generate {cardCount} Flashcards
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                >
                  Remove file
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSelectSection = () => (
    <div className="recite-select">
      <div className="module-header flashcard-module">
        <div className="module-icon">✅</div>
        <h1 className="module-title">{t('moduleHeaders.reciteSelect.title')}</h1>
        <p className="module-subtitle">{t('moduleHeaders.reciteSelect.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="recite" />

      <div className="select-content">
        <div className="select-header">
          <div className="select-info">
            <h3>{currentFlashcardSet?.title}</h3>
            <p>{generatedCards.length} cards generated • {selectedCards.size} selected</p>
          </div>
          <div className="select-actions">
            <button className="select-all-button" onClick={selectAllCards}>
              Select All
            </button>
            <button className="deselect-all-button" onClick={deselectAllCards}>
              Deselect All
            </button>
          </div>
        </div>

        {countNotice && (
          <div className="error-message" style={{ margin: '0 0 16px', padding: '12px 16px', background: '#fff8e1', border: '1px solid #ffe08a', borderRadius: 10, color: '#8a6100' }}>
            ⚠️ {countNotice}
          </div>
        )}

        <div className="cards-grid">
          {generatedCards.map((card, index) => (
            <div
              key={card.id}
              className={`card-item ${selectedCards.has(card.id) ? 'selected' : ''}`}
              onClick={() => toggleCardSelection(card.id)}
            >
              <div className="card-checkbox">
                <input
                  type="checkbox"
                  checked={selectedCards.has(card.id)}
                  onChange={() => toggleCardSelection(card.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="card-content">
                <div className="card-question">
                  <h4>Question {index + 1}</h4>
                  <div className="card-text"><MathText text={card.question} /></div>
                </div>
                <div className="card-answer">
                  <h4>Answer</h4>
                  <div className="card-text"><MathText text={card.answer} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="select-actions-bottom" style={{ flexWrap: 'wrap', gap: 12 }}>
          <button
            className="study-now-button"
            onClick={downloadSelectedCardsTxt}
            disabled={selectedCards.size === 0}
          >
            <span className="button-icon">⬇️</span>
            Download {selectedCards.size} card{selectedCards.size === 1 ? '' : 's'} (.txt)
          </button>
          <button className="back-button" onClick={() => updateView('import')}>
            ✍️ Use My Own Cards
          </button>
        </div>
        <p style={{ textAlign: 'center', color: '#888', fontSize: '0.85rem', marginTop: 8 }}>
          The .txt uses the <code>Q:</code>/<code>A:</code> format — upload it later under “Use My Own Cards”.
          Cards are kept only in this browser tab and are not saved to an account.
        </p>
      </div>
    </div>
  );

  const renderStudySection = () => {
    const isLastCard = currentCardIndex >= currentFlashcards.length - 1;
    return (
    <div className="recite-study">
      <div className="study-header">
        <button className="back-button" onClick={() => updateView('main')}>
          ← Back home
        </button>
        <h2>{currentFlashcardSet?.title}</h2>
        <p>Card {currentCardIndex + 1} of {currentFlashcards.length} (shuffled)</p>
      </div>

      {/* Keyboard shortcut reminder label */}
      <div style={{margin: '0 auto 1rem auto', color:'#888', fontSize:'0.98em', textAlign:'center', maxWidth: 600}}>
        <span>Shortcuts: <b>Space</b> = Show Answer, <b>→</b> / <b>Enter</b> = Next card</span>
      </div>

      {/* Simple progress — no score, no timer */}
      <div className="study-stats">
        <div className="study-progress">
          <div className="progress-text">
            Progress: {currentCardIndex + 1} / {currentFlashcards.length}
          </div>
        </div>
      </div>

      {currentFlashcards.length > 0 && (
        <div className="flashcard-container">
          <div className="flashcard">
            <div className="flashcard-question">
              <h3>Question</h3>
              <div className="card-text"><MathText text={currentFlashcards[currentCardIndex].question} /></div>
            </div>
            {showAnswer && (
              <div className="flashcard-answer">
                <h3>Answer</h3>
                <div className="card-text"><MathText text={currentFlashcards[currentCardIndex].answer} /></div>
              </div>
            )}
          </div>
          <div className="flashcard-actions">
            {!showAnswer ? (
              <button
                className="show-answer-button"
                onClick={() => setShowAnswer(true)}
              >
                Show Answer
              </button>
            ) : (
              <div className="answer-buttons">
                <button className="correct-button" onClick={nextCard}>
                  {isLastCard ? '🎉 Finish' : 'Next card →'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderHistorySection = () => (
    <div className="recite-history">
      <div className="module-header flashcard-module">
        <div className="module-icon">📊</div>
        <h1 className="module-title">{t('moduleHeaders.reciteHistory.title')}</h1>
        <p className="module-subtitle">{t('moduleHeaders.reciteHistory.subtitle')}</p>
      </div>

      <ModuleTopExtras moduleId="recite" />

      {studyHistory && (
        <div className="history-content">
          <div className="history-navigation">
            <button className="back-button" onClick={() => updateView('main')}>
              ← Back to Menu
            </button>
          </div>
          <div className="flashcard-sets">
            <div className="sets-header">
              <h3>Your Flashcard Sets</h3>
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search card sets..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                {/* <span className="search-icon">🔍</span> */}
              </div>
            </div>
            {(!studyHistory.flashcard_sets || studyHistory.flashcard_sets.length === 0) ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h4>{searchQuery ? 'No matching flashcard sets found' : 'No flashcard sets yet'}</h4>
                <p>{searchQuery ? `No sets found matching "${searchQuery}"` : 'Create your first set to get started'}</p>
                {!searchQuery && (
                  <button 
                    className="create-first-set-button"
                    onClick={() => updateView('upload')}
                  >
                    Create First Set
                  </button>
                )}
              </div>
            ) : (
              <div className="sets-grid">
                {studyHistory.flashcard_sets.map((set) => (
                  <div key={set.id} className="set-card">
                    <div className="set-header">
                      {titleEditingSetId === set.id ? (
                        <div className="edit-title-container">
                          <input
                            type="text"
                            className="edit-title-input"
                            value={editingTitle}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>)=>{
                              setEditingTitle(e.target.value);
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                renameFlashcardSet(set.id, editingTitle);
                              }
                            }}
                            onBlur={(e) => {
                              renameFlashcardSet(set.id, editingTitle);
                              setTitleEditingSetId(null);
                              setEditingTitle('');
                            }}
                            autoFocus
                          />
                        </div>
                      ) : (
                        <h4 
                          onDoubleClick={() => {
                            setTitleEditingSetId(set.id);
                            setEditingTitle(set.title);
                          }}
                          className="editable-title"
                        >
                          {set.title}
                        </h4>
                      )}
                      <span className="card-count" onClick={() => openEditView(set.id)} style={{cursor:'pointer'}}>{set.card_count}</span>
                    </div>
                    <p className="set-description">{set.description}</p>
                    <div className="set-stats">
                      <div className="stat">
                        <span className="stat-label">Sessions:</span>
                        <span className="stat-value">{set.total_study_sessions}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Avg Score:</span>
                        <span className="stat-value">{set.average_score.toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="set-actions">
                      <button
                        className="delete-button"
                        onClick={() => deleteFlashcardSet(set.id)}
                        title="Delete"
                        aria-label="Delete flashcard set"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination Controls */}
            {paginationInfo && paginationInfo.total_pages > 1 && (
              <div className="pagination-controls">
                <button 
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!paginationInfo.has_previous}
                >
                  ← Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: paginationInfo.total_pages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      className={`page-number ${pageNum === currentPage ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                
                <button 
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!paginationInfo.has_next}
                >
                  Next →
                </button>
              </div>
            )}
          </div>

          {studyHistory.recent_sessions && studyHistory.recent_sessions.length > 0 && (
            <div className="recent-sessions">
              <h3>Recent Study Sessions</h3>
              <p className="sessions-subtitle">Latest 5 completed study attempts</p>
              <div className="sessions-list">
                {studyHistory.recent_sessions.map((session) => (
                    <div key={session.id} className="session-item">
                      <div className="session-info">
                        <div className="session-title">
                          <h4>{session.flashcard_set.title || 'Untitled Set'}</h4>
                          <span className="session-date">{formatDate(session.completed_at)}</span>
                        </div>
                        <div className="session-details">
                          <span className="session-cards">{session.total_cards} cards studied</span>
                        </div>
                      </div>
                      <div className="session-score">
                        <div className="score-display">
                          <span className="score-value">{session.score}/{session.total_cards}</span>
                          <span className="score-percentage">
                            {((session.score / session.total_cards) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="score-bar">
                          <div 
                            className="score-fill" 
                            style={{ 
                              width: `${(session.score / session.total_cards) * 100}%`,
                              backgroundColor: (session.score / session.total_cards) >= 0.8 ? '#4CAF50' :
                                             (session.score / session.total_cards) >= 0.6 ? '#FF9800' : '#F44336'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderEditSection = () => (
    <div className="recite-edit">
      <div className="history-navigation">
        <button className="back-button" onClick={() => updateView('main')}>← Back</button>
      </div>
      {isLoadingEdit ? (
        <div className="loading-container">Loading set...</div>
      ) : (
        <div className="edit-content" onClick={handleEditBackgroundClick}>
            <div className="edit-header">
            <h3 className="edit-section-title">{currentFlashcardSet?.title}</h3>
            <div className="edit-header-actions">
              <input
                type="file"
                accept=".txt"
                onChange={importFlashcards}
                style={{ display: 'none' }}
                id="import-flashcards-input"
              />
              {/* Clear (text-style) placed left of Import */}
              <button
                className="import-export-button"
                onClick={() => currentFlashcardSet && clearFlashcardSet(currentFlashcardSet.id)}
                title="Clear Progress"
                type="button"
              >
                🔄 Clear
              </button>
              <label htmlFor="import-flashcards-input" className="import-export-button">
                📥 Import
              </label>
              <button
                className="import-export-button"
                onClick={exportFlashcards}
                disabled={!editingCards.length}
              >
                📤 Export
              </button>
            </div>
          </div>
          <div className="cards-grid">
            {editingCards.map((card, idx) => {
              const cardKey = card.id ?? idx; // stable key and id reference
              const isEditable = activeEditIds.has(cardKey);
              return (
                <div
                  key={cardKey}
                  className="card-item"
                  draggable
                  onDragStart={() => handleCardDragStart(idx)}
                  onDragOver={handleCardDragOver}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleCardDrop(idx);
                  }}
                >
                  {/* Card Number Badge */}
                  <div className="card-number-badge">
                    {idx + 1}
                  </div>
                  
                  <div
                    className="card-actions"
                    style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: '8px' }}
                  >
                    <button
                      className="edit-button"
                      title="Edit"
                      type="button"
                      onClick={(e)=>{e.stopPropagation(); toggleCardEditing(cardKey);}}
                    >
                      <EditIcon size={18} />
                    </button>
                    <button
                      className="delete-button"
                      title="Delete"
                      type="button"
                      onClick={(e)=>{e.stopPropagation(); setCardToDelete(card); setShowCardDeleteConfirm(true);}}
                    >
                      <TrashIcon size={18} />
                    </button>
                  </div>
                  <div className="card-content">
                    <textarea
                      className="card-textarea"
                      placeholder="Question"
                      value={card.question}
                      readOnly={!isEditable}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const val = e.target.value;
                        setEditingCards(prev =>
                          prev.map(c => (c === card ? { ...c, question: val } : c))
                        );
                      }}
                      onBlur={() => handleTextareaBlur(card, idx)}
                    />
                    <textarea
                      className="card-textarea"
                      placeholder="Answer"
                      value={card.answer}
                      readOnly={!isEditable}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const val = e.target.value;
                        setEditingCards(prev =>
                          prev.map(c => (c === card ? { ...c, answer: val } : c))
                        );
                      }}
                      onBlur={() => handleTextareaBlur(card, idx)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="edit-actions">
            <button
              className="create-set-button"
              onClick={() => {
                const newCardId = Date.now() * -1;
                setEditingCards(prev => [
                  ...prev,
                  { id: newCardId, question: '', answer: '' },
                ]);
                setActiveEditIds(prev => {
                  const updated = new Set(prev);
                  updated.add(newCardId);
                  return updated;
                });
              }}
            >
              <PlusIcon size={20} /> Add Card
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Delete Confirmation Modal
  const renderDeleteConfirmModal = () => {
    if (!showDeleteConfirm) return null;

    return (
      <div className="modal-overlay" onClick={cancelDelete}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon">
              <TrashIcon size={24} />
            </div>
            <h3>Delete Flashcard Set</h3>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this flashcard set?</p>
            <p className="modal-warning">This action cannot be undone.</p>
          </div>
          <div className="modal-actions">
            <button className="modal-button cancel-button" onClick={cancelDelete}>
              Cancel
            </button>
            <button className="modal-button delete-button" onClick={confirmDelete}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Clear Confirmation Modal
  const renderClearConfirmModal = () => {
    if (!showClearConfirm) return null;

    return (
      <div className="modal-overlay" onClick={cancelClear}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon modal-icon-yellow">
              <ClearIcon size={24} />
            </div>
            <h3>Clear Flashcard Set Progress</h3>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to clear all progress for this flashcard set?</p>
            <p className="modal-warning-yellow">This will reset all study sessions, card performance, and progress data. You can study the set from scratch again.</p>
          </div>
          <div className="modal-actions">
            <button className="modal-button cancel-button" onClick={cancelClear}>
              Cancel
            </button>
            <button className="modal-button clear-button" onClick={confirmClear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Card delete confirmation modal
  const renderCardDeleteConfirmModal = () => {
    if (!showCardDeleteConfirm || !cardToDelete) return null;

    const handleCardDelete = async () => {
      try {
        if (cardToDelete.id && cardToDelete.id > 0) {
          await deleteCardById(cardToDelete.id);
        }
        showToast('Flashcard deleted successfully!', 'success');
      } catch (error) {
        console.error('Failed to delete card', error);
        showToast('Failed to delete flashcard. Please try again.', 'error');
      } finally {
        setEditingCards(prev => prev.filter(c => c !== cardToDelete));
        setActiveEditIds(prev => {
          const updated = new Set(prev);
          const key = cardToDelete.id ?? -1;
          updated.delete(key);
          return updated;
        });
        setShowCardDeleteConfirm(false);
        setCardToDelete(null);
      }
    };

    const cancelCardDelete = () => {
      setShowCardDeleteConfirm(false);
      setCardToDelete(null);
    };

    return (
      <div className="modal-overlay" onClick={cancelCardDelete}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-icon">
              <TrashIcon size={24} />
            </div>
            <h3>Delete Flashcard</h3>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this flashcard?</p>
            <p className="modal-warning">This action cannot be undone.</p>
          </div>
          <div className="modal-actions">
            <button className="modal-button cancel-button" onClick={cancelCardDelete}>
              Cancel
            </button>
            <button className="modal-button delete-button" onClick={handleCardDelete}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render switch
  const renderImportSection = () => {
    const preview = parseFlashcards(importText);
    return (
      <div className="module-container recite-module">
        <div className="module-header">
          <span className="module-icon">✍️</span>
          <h2 className="module-title">{t('moduleHeaders.reciteImport.title')}</h2>
          <p className="module-subtitle">{t('moduleHeaders.reciteImport.subtitle')}</p>
        </div>
        <ModuleTopExtras moduleId="recite" />
        <div className="module-content">
          <div className="content-section">
            <div className="section-header">
              <h3>Paste your cards</h3>
              <p>Format: <code>Q:</code> a question followed by <code>A:</code> its answer, separated by a blank line. Or one card per line as <code>front⇥back</code> (tab) or <code>front,back</code>.</p>
            </div>
            <textarea
              className="content-textarea"
              rows={12}
              placeholder={'Q: What is photosynthesis?\nA: The process plants use to turn sunlight into energy.\n\nQ: What is 2 + 2?\nA: 4'}
              value={importText}
              onChange={(e) => { setImportText(e.target.value); setImportError(''); }}
            />
            {importError && <div className="error-message" style={{ marginTop: 8 }}>⚠️ {importError}</div>}
            {importText.trim() && (
              <div className="word-count-info" style={{ marginTop: 8 }}>
                {preview.length} card{preview.length === 1 ? '' : 's'} detected
              </div>
            )}
            <div className="generation-actions" style={{ flexWrap: 'wrap', gap: 12 }}>
              <button className="btn btn-primary" onClick={handleImportText} disabled={!preview.length}>
                Revise {preview.length || ''} Card{preview.length === 1 ? '' : 's'}
              </button>
              <button className="btn btn-secondary" onClick={() => importFileRef.current?.click()}>
                Upload .txt / .csv
              </button>
              <input
                ref={importFileRef}
                type="file"
                accept=".txt,.csv,text/plain,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <button className="btn btn-secondary" onClick={() => downloadText('flashcard-template.txt', FLASHCARD_TEMPLATE)}>
                Download template
              </button>
              <button className="btn btn-secondary" onClick={() => updateView('main')}>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentView = () => {
    switch(currentView){
      case 'edit': return renderEditSection();
      case 'main': return renderMainMenu();
      case 'upload': return renderUploadSection();
      case 'import': return renderImportSection();
      case 'select': return renderSelectSection();
      case 'study': return renderStudySection();
      default: return renderMainMenu();
    }
  };

  // Ensure persistent saves on navigation
  useEffect(() => {
    const saveAllEditingCards = async () => {
      for (const card of editingCards) {
        await updateOrCreateCard(card);
      }
    };

    const handlePopState = () => {
      if (currentView === 'edit') {
        saveAllEditingCards();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentView === 'edit') {
        // Fire and forget save
        saveAllEditingCards();
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentView, editingCards]);

  // Import/Export functionality
  const exportFlashcards = () => {
    if (!editingCards.length) {
      showToast('No flashcards to export', 'error');
      return;
    }

    // Format: Question\nAnswer\n---\nQuestion\nAnswer\n---
    const content = editingCards
      .map(card => `${card.question}\n${card.answer}`)
      .join('\n---\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentFlashcardSet?.title || 'flashcards'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showToast('Flashcards exported successfully!', 'success');
  };

  const importFlashcards = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.txt')) {
      showToast('Please select a .txt file', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const cards = content.split('\n---\n').filter(block => block.trim());
        
        const importedCards: Flashcard[] = cards.map((block, index) => {
          const lines = block.trim().split('\n');
          const question = lines[0] || '';
          const answer = lines.slice(1).join('\n') || '';
          
          return {
            id: Date.now() * -1 - index, // Negative IDs for new cards
            question: question.trim(),
            answer: answer.trim()
          };
        }).filter(card => card.question && card.answer);

        if (importedCards.length === 0) {
          showToast('No valid flashcards found in the file', 'error');
          return;
        }

        // Add new cards to existing ones instead of replacing
        setEditingCards(prev => [...prev, ...importedCards]);
        showToast(`Added ${importedCards.length} new flashcards successfully!`, 'success');
      } catch (error) {
        console.error('Error parsing import file:', error);
        showToast('Error parsing the import file. Please check the format.', 'error');
      }
    };

    reader.onerror = () => {
      showToast('Error reading the file', 'error');
    };

    reader.readAsText(file);
    
    // Reset the input
    event.target.value = '';
  };

  const handleTextareaBlur = async (card: Flashcard, index: number) => {
    const saved = await updateOrCreateCard(card);
    if (saved && saved.id && saved.id !== card.id) {
      setEditingCards(prev => {
        const updated = [...prev];
        updated[index] = {...saved};
        return updated;
      });
    }
  };

  // Add after other useEffects
  useEffect(() => {
    if (currentView !== 'study') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;
      if (!currentFlashcards.length) return;
      if (!showAnswer && e.code === 'Space') {
        e.preventDefault();
        setShowAnswer(true);
      } else if (showAnswer && (e.code === 'ArrowRight' || e.code === 'Enter')) {
        e.preventDefault();
        nextCard();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentView, showAnswer, currentFlashcards, currentCardIndex]);

  return (
    <div className="recite-module">
      {renderCurrentView()}
      {renderDeleteConfirmModal()}
      {renderClearConfirmModal()}
      {renderCardDeleteConfirmModal()}
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

export default ReciteModule; 